import { convertToImperialDate } from "app/codes";
import React, { useEffect, useMemo, useState } from "react";
import { getPlanning, getAssetPlannings } from "service/events";

// AV-D4: economic columns for the Planning/scheduling table.
// Field names below MUST match the BE Planning model / serializer (fields="__all__"),
// so these flow through automatically — no service change required.
//   cost                 AFE / 成本        (FloatField; pair with `currency`)
//   priority             优先级            (critical..low / 1..n)
//   expected_uplift      预期增产-油        bbl/d  (PE 验收口径)
//   expected_gas_uplift  预期增产-气        Mscf/d (parallel BE; may not be shipped yet)
//   uplift_phase         主增产相          oil|gas|both (optional BE annotation)
//   resource             占用钻修机 / 资源  (rig / workover unit / crew)
// When a field isn't shipped by BE yet every row is undefined → we render an
// em-dash "—" placeholder + a "待 BE" header annotation so PE/QA can see the
// value is pending, not zero.

// Unit labels for expected production uplift, split by phase (PE 验收口径).
const UPLIFT_UNIT = "bbl/d"; // oil
const GAS_UPLIFT_UNIT = "Mscf/d"; // gas

// Coarse priority rank for sorting (higher = more urgent -> sorts to top when
// descending). Supports both word and numeric priorities; unknown -> 0.
const PRIORITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };
function priorityRank(p) {
  if (p == null || p === "") return 0;
  const num = Number(p);
  if (!Number.isNaN(num)) return num;
  return PRIORITY_RANK[String(p).toLowerCase()] || 0;
}

// Render a possibly-missing economic value. Missing -> em-dash placeholder.
function placeholder(value, suffix) {
  if (value == null || value === "") return "—";
  return suffix ? `${value} ${suffix}` : value;
}

// Whether ANY loaded row carries a value for the given field. When the BE field
// is not yet shipped every row is undefined, and we surface a "待 BE" tag in the
// header so reviewers know the column is wired but the data source is pending.
function fieldReady(rows, key) {
  return (
    Array.isArray(rows) && rows.some((r) => r[key] != null && r[key] !== "")
  );
}

// Numeric coercion for sorting; non-numeric / missing -> null (sorted last).
function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function PlanningTable(props) {
  const { wellId, assetId } = props;
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];
  const [plannings, setPlannings] = useState(null);

  // AV-D4: sort by expected uplift / cost. null = no sort (original order).
  // key: "uplift" | "cost" | "priority"; dir: "asc" | "desc".
  const [sort, setSort] = useState({ key: null, dir: "desc" });

  useEffect(() => {
    if (wellId == null && assetId == null) {
      return;
    }
    showLoadingIcon && showLoadingIcon();
    if (wellId != null) {
      getPlanning(
        wellId,
        (result) => {
          setPlannings(result);
          hideLoadingIcon && hideLoadingIcon();
        },
        (error) => {
          console.error(error);
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    } else if (assetId != null) {
      getAssetPlannings(
        assetId,
        (result) => {
          setPlannings(result);
          hideLoadingIcon && hideLoadingIcon();
        },
        (error) => {
          console.error(error);
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    }
  }, [wellId, assetId, hideLoadingIcon, showLoadingIcon]);

  const upliftReady = fieldReady(plannings, "expected_uplift");
  const gasUpliftReady = fieldReady(plannings, "expected_gas_uplift");
  const costReady = fieldReady(plannings, "cost");
  const priorityReady = fieldReady(plannings, "priority");
  const resourceReady = fieldReady(plannings, "resource");

  // Toggle a sortable column: first click -> desc, second -> asc, third -> off.
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return { key: null, dir: "desc" };
    });
  };

  // AV-D4: front-end sort fallback. If the BE later returns rows pre-ordered
  // (?ordering=-expected_uplift), the same comparators are idempotent on
  // already-sorted data, so this stays correct either way.
  const sortedPlannings = useMemo(() => {
    if (!Array.isArray(plannings)) return plannings;
    if (!sort.key) return plannings;
    const sign = sort.dir === "asc" ? 1 : -1;
    const valueOf = (row) => {
      if (sort.key === "uplift") return num(row.expected_uplift);
      if (sort.key === "cost") return num(row.cost);
      if (sort.key === "priority") return priorityRank(row.priority);
      return null;
    };
    // Copy before sort (don't mutate state); missing values always sink to bottom.
    return [...plannings].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va - vb) * sign;
    });
  }, [plannings, sort]);

  const sortIndicator = (key) => {
    if (sort.key !== key) return " ↕";
    return sort.dir === "asc" ? " ↑" : " ↓";
  };

  return (
    <>
      <table className="table table-borderless table-centered table-nowrap">
        <thead className="">
          <tr>
            <th style={{ width: "1.25rem" }}>
              <div className="d-flex flex-row align-items-center form-check align-middle">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="customCheck1"
                />
                <label className="form-check-label" htmlFor="customCheck1">
                  &nbsp;
                </label>
              </div>
            </th>
            <th>Well</th>
            <th>Event</th>
            <th>Event Type</th>
            <th>Start Date</th>
            <th>Duration(Days)</th>
            {/* AV-D4 economic columns */}
            <th
              role="button"
              onClick={() => toggleSort("cost")}
              style={{ cursor: "pointer" }}
              title="按成本排序 / Sort by cost"
            >
              AFE / Cost
              {!costReady && (
                <span className="text-muted"> (待 BE)</span>
              )}
              {sortIndicator("cost")}
            </th>
            <th
              role="button"
              onClick={() => toggleSort("priority")}
              style={{ cursor: "pointer" }}
              title="按优先级排序 / Sort by priority"
            >
              Priority
              {!priorityReady && (
                <span className="text-muted"> (待 BE)</span>
              )}
              {sortIndicator("priority")}
            </th>
            <th
              role="button"
              onClick={() => toggleSort("uplift")}
              style={{ cursor: "pointer" }}
              title="按预期增油排序 / Sort by expected oil uplift"
            >
              {/* PE 验收点: oil uplift unit is bbl/d */}
              Exp. Oil Uplift ({UPLIFT_UNIT})
              {!upliftReady && (
                <span className="text-muted"> (待 BE)</span>
              )}
              {sortIndicator("uplift")}
            </th>
            <th title="预期增气 / Expected gas uplift (parallel BE)">
              {/* gas uplift unit is Mscf/d */}
              Exp. Gas Uplift ({GAS_UPLIFT_UNIT})
              {!gasUpliftReady && (
                <span className="text-muted"> (待 BE)</span>
              )}
            </th>
            <th title="占用钻修机 / 资源 (Rig / Workover unit)">
              Rig / Resource
              {!resourceReady && <span className="text-muted"> (待 BE)</span>}
            </th>
            <th>Status</th>
            <th>LeadPerson</th>
            <th>Contact</th>
          </tr>
        </thead>
        {sortedPlannings && (
          <tbody>
            {sortedPlannings.map((item, index) => {
              return (
                <tr key={index}>
                  <td style={{ width: "1.25rem" }}>
                    <div className="d-flex flex-row align-items-center form-check align-middle">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={"customCheck" + item.event}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={"customCheck" + item.event}
                      >
                        &nbsp;
                      </label>
                    </div>
                  </td>
                  <td>
                    <a href="javascript: void(0);" className="">
                      {item.well_name}
                    </a>{" "}
                  </td>
                  <td>{item.event}</td>
                  <td>{item.event_type}</td>
                  <td>{convertToImperialDate(item.start_date)}</td>
                  <td>
                    <div className="circle-pill">{item.duration}</div>
                  </td>
                  {/* AV-D4 economic columns */}
                  <td>
                    {placeholder(
                      item.cost,
                      item.cost != null && item.cost !== "" ? item.currency : null
                    )}
                  </td>
                  <td>{placeholder(item.priority)}</td>
                  {/* oil uplift in bbl/d (PE 验收口径) */}
                  <td>{placeholder(item.expected_uplift, UPLIFT_UNIT)}</td>
                  {/* gas uplift in Mscf/d (parallel BE; placeholder until shipped) */}
                  <td>{placeholder(item.expected_gas_uplift, GAS_UPLIFT_UNIT)}</td>
                  <td>{placeholder(item.resource)}</td>
                  <td>{item.status}</td>
                  <td>{item.lead}</td>
                  <td>
                    <button className="btn-custom">{item.contact}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </>
  );
}

export default PlanningTable;
